<template>
  <div>
    <!-- PAGE HEADER -->
    <page-header
      :title="items.length > 0 ? items[0].fields.title : ''"
      :bgImage="
        items.length > 0 ? items[0].fields.blogHeader.fields.file.url : ''
      "
    >
      <blog-tags v-if="items.length > 0" :tags="items[0].fields.tags" />
    </page-header>

    <!-- BLOG POST -->
    <section class="blog-post-wrapper">
      <div class="container">
        <div class="row">
          <div v-if="items.length > 0" class="col-md-8 post-content">
            <div class="post-header">
              <img src="/img/brand/ag-logo.png" alt="Art Grab" />
              <div class="details">
                <div class="date">
                  <span class="details-label">Posted:</span>
                  {{ getDate(items[0].fields.date) }}
                </div>
                <div class="author">
                  <span class="details-label">By:</span>&nbsp;
                  <router-link
                    :to="
                      '/blog?author=' + encodeURI(items[0].fields.authorName)
                    "
                    >{{ items[0].fields.authorName }}</router-link
                  >
                </div>
              </div>
            </div>
            <RichTextRenderer
              :document="items[0].fields.blogContent"
              :nodeRenderers="renderNodes"
            />
          </div>
          <div class="col-md-4 blog-sidebar">
            <blog-sidebar />
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ CTA -->
    <call-to-action-section
      text="Have questions about how you can use licensed artwork? Check out our FAQ."
    >
      <router-link to="/about#faq" class="btn btn-secondary"
        >FREQUENTLY ASKED QUESTIONS</router-link
      >
    </call-to-action-section>

    <!-- NEWSLETTER SIGNUP -->
    <newsletter-signup bg-image="/img/headers/newsletter-bg-2.jpg" />
  </div>
</template>

<script>
import RichTextRenderer from "contentful-rich-text-vue-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";

import {
  PageHeader,
  NewsletterSignup,
  CallToActionSection,
  BlogTags,
  BlogSidebar,
} from "../../components";
const artgrabURL = process.env.VUE_APP_ARTGRAB_URL;

export default {
  name: "blog-page",
  metaInfo() {
    return {
      title: `${this.getBlogTitle()} - Art Grab`,
      meta: [
        {
          vmid: "twitter:card",
          name: "twitter:card",
          content: `summary_large_image`,
        },
        {
          vmid: "twitter:title",
          name: "twitter:title",
          content: `${this.getBlogTitle()} - Art Grab`,
        },
        {
          vmid: "twitter:description",
          name: "twitter:description",
          content: `${this.getPostSnippet()}`,
        },
        {
          vmid: "twitter:image",
          name: "twitter:image",
          content: `${this.getBlogHeader()}`,
        },
        {
          vmid: "og:url",
          property: "og:url",
          content: `${artgrabURL}/blog/${this.getPostUrl()}`,
        },
        {
          vmid: "og:title",
          property: "og:title",
          content: `${this.getBlogTitle()} - Art Grab`,
        },
        {
          vmid: "og:description",
          property: "og:description",
          content: `${this.getPostSnippet()}`,
        },
        {
          vmid: "og:image",
          property: "og:image",
          content: `${this.getBlogHeader()}`,
        },
        { vmid: "og:type", property: "og:type", content: "article" },
        {
          vmid: "description",
          property: "description",
          content: `${this.getPostSnippet()}`,
        },
      ],
      link: [
        { rel: "canonical", href: `${artgrabURL}/blog/${this.getPostUrl()}` },
      ],
    };
  },
  components: {
    PageHeader,
    NewsletterSignup,
    CallToActionSection,
    BlogTags,
    RichTextRenderer,
    BlogSidebar,
  },
  data() {
    return {
      items: [],
      loading: false,
      renderNodes: {
        [BLOCKS.EMBEDDED_ASSET]: (node) => (
          <img
            class="img-fluid mx-auto mt-3 mb-3"
            src={`${node.data.target.fields.file.url}`}
          />
        ),
        [INLINES.EMBEDDED_ENTRY]: (node) => (
          <div class="text-center">
            {node.data.target.fields.isInternal ? (
              <router-link
                to={`${node.data.target.fields.buttonUrl}`}
                class="btn btn-primary mb-3"
              >
                {node.data.target.fields.buttonText}
              </router-link>
            ) : (
              <a
                href={`${node.data.target.fields.buttonUrl}`}
                class="btn btn-primary mb-3"
              >
                {node.data.target.fields.buttonText}
              </a>
            )}
          </div>
        ),
      },
      month: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
    };
  },
  methods: {
    getDate(date) {
      const d = new Date(date);
      return `${
        this.month[d.getUTCMonth()]
      } ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
    },
    getBlogTitle() {
      if (this.items.length > 0) {
        return this.items[0].fields.title;
      }
      return "Blog";
    },
    getPostUrl() {
      if (this.items.length > 0) {
        return this.items[0].fields.postUrl;
      }
      return "https://artgrab.co/blog";
    },
    getBlogHeader() {
      if (this.items.length > 0) {
        return `https:${this.items[0].fields.blogHeader.fields.file.url}`;
      }
      return "https://i.imgur.com/gp9AfzO.jpeg";
    },
    getPostSnippet() {
      if (this.items.length > 0) {
        return `${this.items[0].fields.snippet.substring(0, 120)}...`;
      }
      return "All the latest news and info about Art Grab, your favorite artists, and customer projects.";
    },
  },
  mounted() {
    this.$store.commit("setLayout", "page-layout");
  },
  created() {
    this.$contentful
      .getEntries({
        "fields.postUrl": this.$route.params.title,
        content_type: "blogPost",
      })
      .then((response) => {
        this.items = response.items;
      })
      .catch((err) => console.error(err));
  },
  watch: {
    $route: function () {
      this.$contentful
        .getEntries({
          "fields.postUrl": this.$route.params.title,
          content_type: "blogPost",
        })
        .then((response) => {
          this.items = response.items;
        })
        .catch((err) => console.error(err));
    },
  },
};
</script>

<style scoped lang="scss">
.blog-post-wrapper {
  padding: 4.5rem 1rem;

  .post-header {
    display: flex;

    img {
      width: 50px;
      height: 50px;
      border-radius: 25px;
    }

    .details {
      margin-left: 1rem;
      margin-bottom: 3rem;

      .details-label {
        font-weight: bold;
      }
    }
  }

  .post-content {
    overflow-wrap: break-word;
    word-wrap: break-word;
  }
}
</style>
